// ============================================================================
// FSA DEPRECIATION TAB  (ADR-0006 — Depreciation Calculation working paper)
// ============================================================================
// Input-first flow: 1·Additions register → 2·Disposals → 3·Book schedule
// (WDV or SLM per class, rev 7; computed result view, expandable to
// entries) → 4·IT block
// schedule → 5·Deferred tax. The UI computes on every edit (depCalcEngine)
// and Save persists every derived value verbatim (save-compute-display-read).
// Ledger references are optional traceability; the soft per-class TB
// reconciliation hint is informational only and never blocks Save.

import React, { useCallback, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import notificationService from 'services/notificationService';
import { fsaDepreciationCalcService } from 'api/services/fsa/fsaDepreciationCalcService';
import { fsaCorporateFactsService } from 'api/services/fsa/fsaCorporateFactsService';
import { useFsaDepreciationCalc } from 'hooks/api/fsa/useFsaDepreciationCalc';
import FsaDepAdditionModal from 'components/modals/modules/fsa/FsaDepAdditionModal';
import FsaDepDisposalModal from 'components/modals/modules/fsa/FsaDepDisposalModal';
import FsaDepSettingsModal, { type DepSettings } from 'components/modals/modules/fsa/FsaDepSettingsModal';
import type { ID } from 'api/types';
import type { DepCalcContext, DepClassConfig, DepMethod } from 'api/types/fsa/depreciationCalc.types';
import {
  buildBookTaxReconciliation,
  buildDraft,
  draftToSaveRequest,
  effectiveMethod,
  effectiveResidualPct,
  entryConfigKey,
  newLocalId,
  rateFromLife,
  recomputeDraft,
  type DepDraft,
  type DraftAddition,
  type DraftDisposal,
} from './depCalcEngine';

interface Props {
  engagementId: ID;
  tabId?: string;
  moduleName?: string;
  /**
   * TRUE while this tab is the visible workspace tab. The component stays
   * mounted across rail navigation (draft preservation), so this is the
   * signal to re-pull the server context — e.g. a custom class declared in
   * the Notes tab must appear here without a manual refresh.
   */
  isActive?: boolean;
}

type SectionKey = 'additions' | 'disposals' | 'book' | 'it' | 'dt' | 'recon';

const FsaDepreciationTab: React.FC<Props> = ({ engagementId, isActive = true }) => {
  const { workspace, loading, error, refetch } = useFsaDepreciationCalc(engagementId);

  const [draft, setDraft] = useState<DepDraft | null>(null);
  const snapshotRef = useRef('');
  const loadedForRef = useRef<string | null>(null);
  // Auto-save plumbing — every mutation persists after a short debounce so
  // entries are never lost by leaving the screen (user requirement). The
  // save response is NOT re-seeded into the draft (that would clobber
  // in-flight edits); only the row version and snapshot advance.
  const draftRef = useRef<DepDraft | null>(null);
  draftRef.current = draft;
  const rowVersionRef = useRef(0);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savingRef = useRef(false);
  const [saveTick, setSaveTick] = useState(0);

  // Build the editable draft once per loaded workspace instance. The
  // recomputed state (not the raw saved one) is the dirty-tracking baseline,
  // so a live TB change refreshing opening_source doesn't flag "unsaved".
  const context: DepCalcContext | null = workspace?.context ?? null;
  React.useEffect(() => {
    if (!workspace) return;
    const key = `${workspace.header.id}:${workspace.header.updatedAt ?? ''}:${workspace.header.rowVersion}`;
    if (loadedForRef.current === key) return;
    loadedForRef.current = key;
    const initial = recomputeDraft(buildDraft(workspace), workspace.context);
    snapshotRef.current = JSON.stringify(initial);
    rowVersionRef.current = workspace.header.rowVersion;
    setDraft(initial);
  }, [workspace]);

  const isDirty = useMemo(
    () => draft != null && snapshotRef.current !== '' && JSON.stringify(draft) !== snapshotRef.current,
    // saveTick forces re-evaluation after an auto-save advances the snapshot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [draft, saveTick],
  );

  const applyDraft = useCallback((mutate: (prev: DepDraft) => DepDraft) => {
    if (!context) return;
    setDraft(prev => (prev ? recomputeDraft(mutate(prev), context) : prev));
  }, [context]);

  // ---- sub-tab navigation — one full-height section at a time. The
  // stacked-sections layout proved unworkable (five clipped tables on one
  // shared scroll); each section now owns the panel and its own scroll.
  const [activeSection, setActiveSection] = useState<SectionKey>('additions');

  // ---- expandable class rows (book schedule) -------------------------------
  const [expandedClasses, setExpandedClasses] = useState<Set<string>>(new Set());
  const toggleClass = useCallback((code: string) => {
    setExpandedClasses(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      return next;
    });
  }, []);

  // ---- expandable IT block rows (review drill-down) ------------------------
  const [expandedBlocks, setExpandedBlocks] = useState<Set<string>>(new Set());
  const toggleBlock = useCallback((key: string) => {
    setExpandedBlocks(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  // ---- modals --------------------------------------------------------------
  const [additionModal, setAdditionModal] = useState<{ open: boolean; entry: DraftAddition | null }>({ open: false, entry: null });
  const [disposalModal, setDisposalModal] = useState<{ open: boolean; entry: DraftDisposal | null }>({ open: false, entry: null });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ kind: 'addition' | 'disposal'; localId: string; label: string } | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [exporting, setExporting] = useState<'pdf' | 'excel' | null>(null);

  const handleSaveAddition = useCallback((entry: DraftAddition) => {
    applyDraft(prev => {
      const idx = prev.additions.findIndex(a => a.localId === entry.localId);
      const additions = idx >= 0
        ? prev.additions.map(a => (a.localId === entry.localId ? entry : a))
        : [...prev.additions, entry];
      return { ...prev, additions };
    });
  }, [applyDraft]);

  const handleSaveDisposal = useCallback((entry: DraftDisposal) => {
    applyDraft(prev => {
      const idx = prev.disposals.findIndex(d => d.localId === entry.localId);
      const disposals = idx >= 0
        ? prev.disposals.map(d => (d.localId === entry.localId ? entry : d))
        : [...prev.disposals, entry];
      return { ...prev, disposals };
    });
  }, [applyDraft]);

  const handleConfirmDelete = useCallback(() => {
    if (!deleteTarget) return;
    applyDraft(prev => deleteTarget.kind === 'addition'
      ? { ...prev, additions: prev.additions.filter(a => a.localId !== deleteTarget.localId) }
      : { ...prev, disposals: prev.disposals.filter(d => d.localId !== deleteTarget.localId) });
    setDeleteTarget(null);
  }, [deleteTarget, applyDraft]);

  const handleApplySettings = useCallback((s: DepSettings) => {
    applyDraft(prev => ({
      ...prev,
      roundingPrecision: s.roundingPrecision,
      taxRate: s.taxRate,
      bookProrataConvention: s.bookProrataConvention,
      bookRatesFollowIt: s.bookRatesFollowIt,
      dtEnabled: s.dtEnabled,
      depToDisposalDate: s.depToDisposalDate,
    }));
  }, [applyDraft]);

  const updateClass = useCallback((code: string, patch: Partial<DepClassConfig>) => {
    applyDraft(prev => ({
      ...prev,
      classConfig: {
        ...prev.classConfig,
        [code]: { ...prev.classConfig[code], ...patch },
      },
    }));
  }, [applyDraft]);

  // ---- method (rev 7) — WDV / SLM per class; null on a sub-class = inherit.
  // A change of method with a useful life entered re-derives the rate: a
  // WDV rate is wrong on the SLM basis and vice versa. Without a life the
  // typed rate stays and the auditor re-keys it.
  const setClassMethod = useCallback((code: string, method: DepMethod | null) => {
    applyDraft(prev => {
      const cfg = prev.classConfig[code];
      if (!cfg) return prev;
      const next: DepClassConfig = { ...cfg, method };
      const changed = effectiveMethod(next, prev.classConfig) !== (cfg.method_effective ?? 'WDV');
      if (changed && (cfg.useful_life_years ?? 0) > 0) next.rate_from_life = true;
      return { ...prev, classConfig: { ...prev.classConfig, [code]: next } };
    });
  }, [applyDraft]);

  // ---- policy cross-check (rev 7) — Note 2's DEPRECIATION_METHOD answer
  // vs the PPE classes' methods. Informational only; never blocks anything.
  const [policyMethod, setPolicyMethod] = useState<DepMethod | null>(null);
  React.useEffect(() => {
    if (!isActive) return;
    let alive = true;
    fsaCorporateFactsService.get(engagementId)
      .then(r => {
        if (!alive || !r.data) return;
        let answer: unknown;
        try { answer = JSON.parse(r.data.data || '{}')?.note_answers?.DEPRECIATION_METHOD; } catch { answer = undefined; }
        // Unanswered = the policy's default answer (WDV).
        setPolicyMethod(answer === 'SLM' ? 'SLM' : 'WDV');
      })
      .catch(() => { if (alive) setPolicyMethod(null); });
    return () => { alive = false; };
  }, [engagementId, isActive]);

  // ---- sub-classes (rev 6) — depreciation-internal lines under a class ----
  const [subEditor, setSubEditor] = useState<{ parentCode: string; name: string; rate: string } | null>(null);
  const handleConfirmSubClass = useCallback(() => {
    if (!subEditor) return;
    const name = subEditor.name.trim();
    if (!name) {
      notificationService.toast.warning('Enter a sub-class name.');
      return;
    }
    const rate = parseFloat(subEditor.rate);
    const parentCode = subEditor.parentCode;
    applyDraft(prev => ({
      ...prev,
      classConfig: {
        ...prev.classConfig,
        [newLocalId()]: {
          schedule_code: prev.classConfig[parentCode]?.schedule_code,
          parent_class_code: parentCode,
          name,
          book_rate: Number.isFinite(rate) && rate > 0 ? rate : null,
          opening_wdv: 0,
        },
      },
    }));
    setSubEditor(null);
  }, [subEditor, applyDraft]);

  const removeSubClass = useCallback((code: string) => {
    applyDraft(prev => {
      const next = { ...prev.classConfig };
      delete next[code];
      return { ...prev, classConfig: next };
    });
  }, [applyDraft]);

  const updateItBlock = useCallback((enumId: string, patch: { openingWdv?: number; rate?: number; isMarkedExhausted?: boolean }) => {
    applyDraft(prev => ({
      ...prev,
      itBlocks: prev.itBlocks.map(b => (String(b.itBlockEnumId) === enumId ? { ...b, ...patch } : b)),
    }));
  }, [applyDraft]);

  const addItBlockRow = useCallback((enumId: string) => {
    if (!context) return;
    const opt = context.itBlockOptions.find(o => String(o.id) === enumId);
    if (!opt) return;
    applyDraft(prev => prev.itBlocks.some(b => String(b.itBlockEnumId) === enumId)
      ? prev
      : {
          ...prev,
          itBlocks: [...prev.itBlocks, {
            itBlockEnumId: opt.id,
            enumKey: opt.enumKey,
            label: opt.enumValue,
            openingWdv: 0,
            rate: opt.rate,
            isMarkedExhausted: false,
            additionsGe180: null, additionsLt180: null, disposalProceeds: null,
            itDepActual: null, itDepRounded: null,
            closingWdvActual: null, closingWdvRounded: null,
            isBlockNegative: false,
            itProfitLoss: null,
          }],
        });
  }, [applyDraft, context]);

  const removeItBlockRow = useCallback((enumId: string) => {
    applyDraft(prev => ({
      ...prev,
      itBlocks: prev.itBlocks.filter(b => String(b.itBlockEnumId) !== enumId),
    }));
  }, [applyDraft]);

  // ---- auto-save (debounced) ----------------------------------------------
  // Additions/disposals (and every other edit) persist automatically shortly
  // after entry — leaving the screen never loses data. The draft is the
  // source of truth during editing; only the snapshot + row version advance
  // on a successful save.
  const lastFailedJsonRef = useRef<string | null>(null);

  const performSave = useCallback(async (): Promise<boolean> => {
    const d = draftRef.current;
    if (!d) return true;
    const sentJson = JSON.stringify(d);
    if (sentJson === snapshotRef.current) return true;
    if (savingRef.current) return false; // in flight — the effect reschedules
    savingRef.current = true;
    setIsSaving(true);
    try {
      const r = await fsaDepreciationCalcService.save(engagementId, draftToSaveRequest(d, rowVersionRef.current));
      if (r.data) rowVersionRef.current = r.data.header.rowVersion;
      snapshotRef.current = sentJson;
      lastFailedJsonRef.current = null;
      return true;
    } catch (err: any) {
      // Remember the failing payload so the effect doesn't retry-loop with
      // toast spam; the next edit (or Save now) tries again.
      lastFailedJsonRef.current = sentJson;
      notificationService.toast.error(err?.response?.data?.message ?? 'Error saving depreciation calc.');
      return false;
    } finally {
      savingRef.current = false;
      setIsSaving(false);
      setSaveTick(t => t + 1);
    }
  }, [engagementId]);

  React.useEffect(() => {
    if (!draft || snapshotRef.current === '') return;
    const json = JSON.stringify(draft);
    if (json === snapshotRef.current) return;
    if (json === lastFailedJsonRef.current) return; // wait for a change or Save now
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => { void performSave(); }, 1200);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [draft, saveTick, performSave]);

  const handleSaveNow = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    lastFailedJsonRef.current = null;
    void performSave();
  }, [performSave]);

  // The tab stays mounted while hidden — when it becomes visible again,
  // flush any pending save and re-pull the workspace so server-side changes
  // made meanwhile (e.g. a custom class declared in the Notes tab) appear
  // without a manual refresh.
  const prevActiveRef = useRef(isActive);
  React.useEffect(() => {
    const wasActive = prevActiveRef.current;
    prevActiveRef.current = isActive;
    if (!isActive || wasActive) return;
    (async () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      await performSave();
      refetch();
    })();
  }, [isActive, performSave, refetch]);

  // Fold refreshed context into the draft: Book Schedule rows come from
  // draft.classConfig, which recomputeDraft extends with any newly-arrived
  // class options (no-op when nothing changed).
  React.useEffect(() => {
    if (!context) return;
    setDraft(prev => (prev ? recomputeDraft(prev, context) : prev));
  }, [context]);

  // ---- exports (§11) — saved values only -----------------------------------
  const handleExport = useCallback(async (kind: 'pdf' | 'excel') => {
    // Flush any pending auto-save so the export reflects what's on screen.
    if (isDirty || savingRef.current) {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      const ok = await performSave();
      if (!ok) {
        notificationService.toast.warning('Could not save before exporting — try again in a moment.');
        return;
      }
    }
    setExporting(kind);
    try {
      const blob = kind === 'pdf'
        ? await fsaDepreciationCalcService.getPdfBlob(engagementId)
        : await fsaDepreciationCalcService.getExcelBlob(engagementId);
      const url = URL.createObjectURL(blob);
      if (kind === 'pdf') {
        const opened = window.open(url, '_blank');
        if (!opened) notificationService.toast.warning('Pop-up blocked. Allow pop-ups so the PDF can open.', 6000);
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = `FSA-Depreciation-Working-Paper-${new Date().toISOString().slice(0, 10)}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        notificationService.toast.success('Download started.');
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message ?? `Error generating ${kind === 'pdf' ? 'PDF' : 'Excel'}.`);
    } finally {
      setExporting(null);
    }
  }, [engagementId, isDirty, performSave]);

  // ---- derived display helpers ---------------------------------------------
  const p = draft?.roundingPrecision ?? 2;
  const fmt = useCallback((v: number | null | undefined): string => {
    if (v == null) return '—';
    return v.toLocaleString('en-IN', { minimumFractionDigits: p, maximumFractionDigits: p });
  }, [p]);
  /** Auditor presentation — negative values in brackets. */
  const fmtBracket = useCallback((v: number | null | undefined): string => {
    if (v == null) return '—';
    return v < 0 ? `(${fmt(Math.abs(v))})` : fmt(v);
  }, [fmt]);

  const classNameByCode = useMemo(() => {
    const map = new Map<string, string>();
    context?.classOptions.forEach(o => map.set(o.classCode, o.className));
    return map;
  }, [context]);
  const clsName = useCallback((code: string): string => {
    const direct = classNameByCode.get(code);
    if (direct) return direct;
    // Sub-class keys (rev 6) resolve as "Parent — Sub name".
    const cfg = draft?.classConfig[code];
    if (cfg?.parent_class_code) {
      return `${classNameByCode.get(cfg.parent_class_code) ?? cfg.parent_class_code} — ${cfg.name ?? code}`;
    }
    return code;
  }, [classNameByCode, draft]);

  /**
   * Book classes in template order; unknown codes last. Classes show when
   * they have any activity OR any TB presence (so the rate/opening can be
   * set before entering additions); the "Show all classes" toggle reveals
   * every fixed-asset class.
   */
  const [showAllClasses, setShowAllClasses] = useState(false);
  const orderedClasses = useMemo((): Array<{ code: string; cfg: DepClassConfig; isSub: boolean }> => {
    if (!draft || !context) return [];
    const orderIndex = new Map(context.classOptions.map((o, i) => [o.classCode, i]));
    const tbPresence = new Map(context.classOptions.map(o => [o.classCode, o.tbOpeningSum !== 0 || o.tbClosingSum !== 0]));

    const entries = Object.entries(draft.classConfig);
    const subsByParent = new Map<string, Array<[string, DepClassConfig]>>();
    for (const [code, cfg] of entries) {
      if (cfg.parent_class_code) {
        const list = subsByParent.get(cfg.parent_class_code) ?? [];
        list.push([code, cfg]);
        subsByParent.set(cfg.parent_class_code, list);
      }
    }
    const hasActivity = (code: string, cfg: DepClassConfig): boolean =>
      (cfg.opening_wdv ?? 0) !== 0
      || (cfg.additions_total ?? 0) !== 0
      || (cfg.disposals_cost_total ?? 0) !== 0
      // A rate mirrored from the IT block (rev 8) is not activity — otherwise
      // every class with a default block would show.
      || ((cfg.book_rate ?? 0) !== 0 && cfg.rate_source !== 'IT')
      || draft.additions.some(a => entryConfigKey(a) === code)
      || draft.disposals.some(d => entryConfigKey(d) === code);

    const out: Array<{ code: string; cfg: DepClassConfig; isSub: boolean }> = [];
    const parents = entries
      .filter(([, cfg]) => !cfg.parent_class_code)
      .sort(([a], [b]) => (orderIndex.get(a) ?? 9e9) - (orderIndex.get(b) ?? 9e9));
    for (const [code, cfg] of parents) {
      const subs = (subsByParent.get(code) ?? [])
        .sort((x, y) => (x[1].name ?? '').localeCompare(y[1].name ?? ''));
      const shown = showAllClasses
        || subs.length > 0            // a class with sub-classes always shows
        || hasActivity(code, cfg)
        || tbPresence.get(code) === true;
      if (!shown) continue;
      out.push({ code, cfg, isSub: false });
      for (const [sCode, sCfg] of subs) out.push({ code: sCode, cfg: sCfg, isSub: true });
    }
    // Defensive — sub entries whose parent config vanished still render.
    for (const [code, cfg] of entries) {
      if (cfg.parent_class_code && !out.some(o => o.code === code)) {
        out.push({ code, cfg, isSub: true });
      }
    }
    return out;
  }, [draft, context, showAllClasses]);

  // Rev 3: no ledger-based drift — ledger mapping is optional and never the
  // reconciliation basis. Drift vs the NOTE figures arrives with Phase 1b
  // suggestions. The only hints here are IT block cessation notices.
  const exhaustedBlocks = draft?.itBlocks.filter(b => b.isBlockNegative || b.isMarkedExhausted).length ?? 0;

  // Rev 7 — PPE classes in use whose method differs from Note 2's policy
  // answer. The policy speaks for PPE only (intangibles have their own
  // amortisation paragraph), so only NOTE_PPE lines are compared.
  const policyMismatch = useMemo(() => {
    if (!policyMethod) return [] as string[];
    return orderedClasses
      .filter(({ cfg: c }) => c.schedule_code === 'NOTE_PPE' && (c.book_rate ?? 0) > 0
        && (c.method_effective ?? 'WDV') !== policyMethod)
      .map(({ code }) => code);
  }, [orderedClasses, policyMethod]);
  const cappedClasses = orderedClasses.filter(c => c.cfg.dep_capped).length;
  const hintCount = exhaustedBlocks + (policyMismatch.length > 0 ? 1 : 0) + cappedClasses;

  const bookTotals = useMemo(() => {
    const t = { opening: 0, additions: 0, disposals: 0, dep: 0, closing: 0 };
    for (const { cfg } of orderedClasses) {
      t.opening += cfg.opening_wdv ?? 0;
      t.additions += cfg.additions_total ?? 0;
      t.disposals += cfg.disposals_nbv_total ?? 0;
      t.dep += cfg.dep_for_year_rounded ?? 0;
      t.closing += cfg.closing_wdv_rounded ?? 0;
    }
    return t;
  }, [orderedClasses]);

  const itTotals = useMemo(() => {
    const t = { opening: 0, ge: 0, lt: 0, proceeds: 0, dep: 0, pl: 0, closing: 0 };
    for (const b of draft?.itBlocks ?? []) {
      t.opening += b.openingWdv;
      t.ge += b.additionsGe180 ?? 0;
      t.lt += b.additionsLt180 ?? 0;
      t.proceeds += b.disposalProceeds ?? 0;
      t.dep += b.itDepRounded ?? 0;
      t.pl += b.itProfitLoss ?? 0;
      t.closing += b.closingWdvRounded ?? 0;
    }
    return t;
  }, [draft]);

  // Rev 8 — book vs tax statement (pure sums of the computed figures).
  const recon = useMemo(
    () => (draft && context ? buildBookTaxReconciliation(draft, context) : null),
    [draft, context],
  );

  const additionsByClass = useMemo(() => {
    const map = new Map<string, DraftAddition[]>();
    draft?.additions.forEach(a => {
      const list = map.get(a.classCode) ?? [];
      list.push(a);
      map.set(a.classCode, list);
    });
    return map;
  }, [draft]);

  // ---- render --------------------------------------------------------------
  if (loading && !workspace) {
    return <CenterNote text="Loading depreciation workspace…" />;
  }
  if (error && !workspace) {
    return <CenterNote text={error} isError onRetry={refetch} />;
  }
  if (!workspace || !draft || !context || !recon) {
    return <CenterNote text="Depreciation workspace unavailable." />;
  }

  const availableBlockOptions = context.itBlockOptions
    .filter(o => !draft.itBlocks.some(b => String(b.itBlockEnumId) === String(o.id)));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* toolbar */}
      <div style={toolbarStyle}>
        <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
          Rounding: <b>{p} dp</b> · Tax rate: <b>{draft.taxRate != null ? `${draft.taxRate}%` : 'not set'}</b> ·
          Pro-rata: <b>{draft.bookProrataConvention === 'HALF_RATE_180' ? '180-day' : 'day-wise'}</b>
          {draft.bookRatesFollowIt && <> · Book rates: <b>follow IT</b></>}
          {draft.depToDisposalDate && <> · Disposals: <b>dep to sale date</b></>}
          {!draft.dtEnabled && <> · Deferred tax: <b>off</b></>}
        </div>
        {hintCount > 0 && (
          <span style={hintChipStyle} title="Informational only — never blocks Save">
            ⚠ {hintCount} hint{hintCount === 1 ? '' : 's'}
          </span>
        )}
        {isSaving ? (
          <span style={dirtyChipStyle}>Saving…</span>
        ) : isDirty ? (
          <span style={dirtyChipStyle}>pending save…</span>
        ) : (
          <span style={savedChipStyle} title="All changes are persisted automatically">✓ saved</span>
        )}
        <div style={{ flex: 1 }} />
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => handleExport('pdf')}
          disabled={exporting != null} title="Working-paper PDF (reads saved figures)">
          <IconSystem.print size={14} /> {exporting === 'pdf' ? 'Rendering…' : 'Print'}
        </button>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => handleExport('excel')}
          disabled={exporting != null} title="Working-paper Excel (reads saved figures)">
          <IconSystem.download size={14} /> {exporting === 'excel' ? 'Building…' : 'Excel'}
        </button>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setSettingsOpen(true)}>
          <IconSystem.settings size={14} /> Settings
        </button>
        <button type="button" className="btn btn-secondary btn-sm" onClick={refetch} disabled={loading} title="Reload from server">
          <IconSystem.refresh size={14} />
        </button>
        <button type="button" className="btn btn-primary btn-sm" onClick={handleSaveNow} disabled={!isDirty || isSaving} title="Changes save automatically — this saves immediately">
          {isSaving ? 'Saving…' : 'Save now'}
        </button>
      </div>

      {/* sub-tab bar */}
      <div style={subTabBarStyle}>
        <SubTab label="1 · Additions" badge={draft.additions.length > 0 ? String(draft.additions.length) : undefined}
          active={activeSection === 'additions'} onClick={() => setActiveSection('additions')} />
        <SubTab label="2 · Disposals" badge={draft.disposals.length > 0 ? String(draft.disposals.length) : undefined}
          active={activeSection === 'disposals'} onClick={() => setActiveSection('disposals')} />
        <SubTab label="3 · Book Schedule" warn={policyMismatch.length > 0 || cappedClasses > 0}
          active={activeSection === 'book'} onClick={() => setActiveSection('book')} />
        <SubTab label="4 · IT Schedule" warn={exhaustedBlocks > 0}
          active={activeSection === 'it'} onClick={() => setActiveSection('it')} />
        <SubTab label={draft.dtEnabled ? '5 · Deferred Tax' : '5 · Deferred Tax (off)'}
          active={activeSection === 'dt'} onClick={() => setActiveSection('dt')} />
        <SubTab label="6 · Book vs Tax"
          active={activeSection === 'recon'} onClick={() => setActiveSection('recon')} />
      </div>

      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: '0.75rem' }}>

        {/* 1 · ADDITIONS */}
        {activeSection === 'additions' && (
        <Section
          title="Asset Additions"
          subtitle="One row per asset purchased during the year — drives both the book and the Income Tax computation."
          action={
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setAdditionModal({ open: true, entry: null })}>
              <IconSystem.add size={14} /> Addition
            </button>
          }
        >
          {draft.additions.length === 0 ? (
            <EmptyNote text="No additions entered yet. Additions drive both the book and the Income Tax computation." />
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <Th>Description</Th><Th>Ledger</Th><Th>Addn. Date</Th><Th>Put-to-use</Th>
                    <Th right>Amount</Th><Th right>Days</Th><Th>IT Block</Th><Th right>Book Dep.</Th><Th right>IT Dep.*</Th><Th> </Th>
                  </tr>
                </thead>
                <tbody>
                  {Array.from(additionsByClass.entries()).map(([code, list]) => (
                    <React.Fragment key={code}>
                      <tr style={groupRowStyle}>
                        <td colSpan={4} style={{ ...tdStyle, fontWeight: 700 }}>{clsName(code)}</td>
                        <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700 }}>{fmt(list.reduce((s, a) => s + a.amount, 0))}</td>
                        <td colSpan={2} style={tdStyle} />
                        <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700 }}>{fmt(list.reduce((s, a) => s + (a.bookDepRounded ?? 0), 0))}</td>
                        <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700 }}>{fmt(list.reduce((s, a) => s + (a.itDepIndicativeRounded ?? 0), 0))}</td>
                        <td style={tdStyle} />
                      </tr>
                      {list.map(a => (
                        <tr key={a.localId}>
                          <td style={tdStyle}>
                            {a.description}
                            {a.subClassLocalId != null && (
                              <span style={subClassChipStyle}>
                                {draft.classConfig[String(a.subClassLocalId)]?.name ?? 'sub-class'}
                              </span>
                            )}
                          </td>
                          <td style={{ ...tdStyle, color: 'var(--text-secondary)' }}>{a.ledgerName ?? '—'}</td>
                          <td style={tdStyle}>{fmtDate(a.additionDate)}</td>
                          <td style={tdStyle}>{fmtDate(a.putToUseDate)}</td>
                          <td style={numTd}>{fmt(a.amount)}</td>
                          <td style={numTd}>
                            {a.daysUsed ?? '—'}
                            {a.isLt180Days && <span style={lt180ChipStyle} title="Put to use < 180 days — IT half rate">&lt;180d</span>}
                          </td>
                          <td style={{ ...tdStyle, fontSize: '0.72rem' }}>{blockLabel(context, a.itBlockEnumId)}</td>
                          <td style={numTd}>{fmt(a.bookDepRounded)}</td>
                          <td style={{ ...numTd, color: 'var(--text-secondary)' }}>{fmt(a.itDepIndicativeRounded)}</td>
                          <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                            <RowBtn title="Edit" onClick={() => setAdditionModal({ open: true, entry: a })}><IconSystem.edit size={13} /></RowBtn>
                            <RowBtn title="Remove" danger onClick={() => setDeleteTarget({ kind: 'addition', localId: a.localId, label: a.description })}><IconSystem.trash size={13} /></RowBtn>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
              <div style={footNoteStyle}>* Per-addition IT depreciation is indicative — the IT block schedule (section 4) is authoritative.</div>
            </div>
          )}
        </Section>
        )}

        {/* 2 · DISPOSALS */}
        {activeSection === 'disposals' && (
        <Section
          title="Disposals"
          subtitle={draft.depToDisposalDate
            ? 'Assets sold/discarded during the year — enter accumulated depreciation up to the start of the year; depreciation to the sale date, WDV at sale and profit/(loss) compute automatically; proceeds reduce the selected IT block.'
            : 'Assets sold/discarded during the year — NBV and profit/(loss) compute automatically; proceeds reduce the selected IT block.'}
          action={
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setDisposalModal({ open: true, entry: null })}>
              <IconSystem.add size={14} /> Disposal
            </button>
          }
        >
          {draft.disposals.length === 0 ? (
            <EmptyNote text="No disposals entered. Profit/(loss) on disposal is computed automatically from cost, accumulated depreciation and proceeds." />
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <Th>Description</Th><Th>Class</Th><Th>Ledger</Th><Th right>Cost</Th>
                    <Th right>{draft.depToDisposalDate ? 'Accum. Dep. (opening)' : 'Accum. Dep.'}</Th>
                    {draft.depToDisposalDate && <Th right>Dep. to sale date</Th>}
                    <Th right>{draft.depToDisposalDate ? 'WDV at sale' : 'NBV'}</Th><Th>Date</Th><Th right>Proceeds</Th><Th>IT Block</Th><Th right>Profit/(Loss)</Th><Th> </Th>
                  </tr>
                </thead>
                <tbody>
                  {draft.disposals.map(d => (
                    <tr key={d.localId}>
                      <td style={tdStyle}>{d.description}</td>
                      <td style={{ ...tdStyle, fontSize: '0.72rem' }}>{clsName(entryConfigKey(d))}</td>
                      <td style={{ ...tdStyle, color: 'var(--text-secondary)' }}>{d.ledgerName ?? '—'}</td>
                      <td style={numTd}>{fmt(d.originalCost)}</td>
                      <td style={numTd}>{fmt(d.accumulatedDepreciation)}</td>
                      {draft.depToDisposalDate && <td style={numTd}>{fmt(d.depToDisposalRounded)}</td>}
                      <td style={numTd}>{fmt(d.netBookValue)}</td>
                      <td style={tdStyle}>{fmtDate(d.disposalDate)}</td>
                      <td style={numTd}>{fmt(d.disposalProceeds)}</td>
                      <td style={{ ...tdStyle, fontSize: '0.72rem' }}>{blockLabel(context, d.itBlockEnumId)}</td>
                      <td style={{ ...numTd, color: (d.profitLossOnDisposal ?? 0) < 0 ? '#dc2626' : '#16a34a' }}>
                        {(d.profitLossOnDisposal ?? 0) < 0 ? `(${fmt(Math.abs(d.profitLossOnDisposal ?? 0))})` : fmt(d.profitLossOnDisposal)}
                      </td>
                      <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                        <RowBtn title="Edit" onClick={() => setDisposalModal({ open: true, entry: d })}><IconSystem.edit size={13} /></RowBtn>
                        <RowBtn title="Remove" danger onClick={() => setDeleteTarget({ kind: 'disposal', localId: d.localId, label: d.description })}><IconSystem.trash size={13} /></RowBtn>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>
        )}

        {/* 3 · BOOK SCHEDULE */}
        {activeSection === 'book' && (
        <Section
          title="Book Depreciation Schedule"
          subtitle="Computed result view — Method, Opening WDV and Rate % are editable here (✎); SLM classes are entered on their cost basis in the row beneath; rows expand to their underlying additions/disposals."
          action={
            <label
              style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.75rem', color: 'var(--text-secondary)', cursor: 'pointer', userSelect: 'none' }}
            >
              <input
                type="checkbox"
                checked={showAllClasses}
                onChange={(e) => setShowAllClasses(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              Show all classes
            </label>
          }
        >
          {policyMismatch.length > 0 && (
            <div style={policyHintStyle}>
              ⚠ Note 2's accounting policy says <b>{policyMethod === 'SLM' ? 'straight line' : 'written down value'}</b>, but{' '}
              {policyMismatch.length === 1 ? 'this PPE class uses' : `${policyMismatch.length} PPE classes use`}{' '}
              {policyMethod === 'SLM' ? 'WDV' : 'SLM'}: {policyMismatch.map(clsName).join(', ')}. Change the class method here, or
              the policy answer in Note 2. Where PPE classes deliberately use both methods, word Note 2's depreciation paragraph to say so.
            </div>
          )}
          {orderedClasses.length === 0 ? (
            <EmptyNote text="No fixed-asset classes with activity yet. Classes appear when TB ledgers are classified under PPE / Intangibles, or when you enter additions." />
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <Th>Class</Th><Th>Method ✎</Th><Th right>Opening WDV ✎</Th><Th right>Additions</Th>
                    <Th right>{draft.depToDisposalDate ? 'Disposals (WDV at sale)' : 'Disposals (NBV)'}</Th>
                    <Th right>Rate % ✎</Th><Th right>Useful life (yrs) ✎</Th><Th right>Dep. for Year</Th><Th right>Closing WDV</Th>
                  </tr>
                </thead>
                <tbody>
                  {orderedClasses.map(({ code, cfg, isSub }) => {
                    const expanded = expandedClasses.has(code);
                    const isSlm = cfg.method_effective === 'SLM';
                    const parentMethod = isSub && cfg.parent_class_code
                      ? draft.classConfig[cfg.parent_class_code]?.method_effective ?? 'WDV'
                      : null;
                    const residual = effectiveResidualPct(cfg, draft.classConfig);
                    const derivable = rateFromLife(cfg.method_effective ?? 'WDV', cfg.useful_life_years, residual) != null;
                    const entries = [
                      ...draft.additions.filter(a => entryConfigKey(a) === code).map(a => ({ kind: 'addition' as const, a })),
                      ...draft.disposals.filter(d => entryConfigKey(d) === code).map(d => ({ kind: 'disposal' as const, d })),
                    ];
                    return (
                      <React.Fragment key={code}>
                        <tr>
                          <td style={{ ...tdStyle, cursor: entries.length > 0 ? 'pointer' : 'default', paddingLeft: isSub ? 26 : tdStyle.padding as any }}
                              onClick={() => entries.length > 0 && toggleClass(code)}>
                            {entries.length > 0 && (
                              <span style={{ marginRight: 6, display: 'inline-block', transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.12s' }}>▸</span>
                            )}
                            {isSub ? (
                              <>
                                <span style={{ color: 'var(--text-secondary)' }}>↳ </span>
                                {cfg.name ?? code}
                                {entries.length === 0 && (
                                  <RowBtn title="Remove sub-class (no entries reference it)" danger onClick={() => removeSubClass(code)}>
                                    <IconSystem.x size={12} />
                                  </RowBtn>
                                )}
                              </>
                            ) : (
                              <>
                                <b>{clsName(code)}</b>
                                <RowBtn title="Add a sub-class with its own rate (e.g. Factory Building under Building)"
                                  onClick={() => setSubEditor({ parentCode: code, name: '', rate: '' })}>
                                  <IconSystem.add size={12} />
                                </RowBtn>
                              </>
                            )}
                          </td>
                          <td style={tdStyle}>
                            <select
                              className="form-select"
                              style={methodSelectStyle}
                              value={cfg.method ?? (isSub ? '' : 'WDV')}
                              onChange={(e) => setClassMethod(code, (e.target.value || null) as DepMethod | null)}
                              title="Depreciation method for this class"
                            >
                              {isSub && <option value="">As class ({parentMethod})</option>}
                              <option value="WDV">WDV</option>
                              <option value="SLM">SLM</option>
                            </select>
                          </td>
                          <td style={numTd}>
                            {isSlm ? (
                              <span title="SLM: cost − accumulated depreciation — enter them in the row beneath">
                                {fmt(cfg.opening_wdv)}
                              </span>
                            ) : (
                              <CellInput
                                value={cfg.opening_wdv ?? 0}
                                onCommit={(v) => updateClass(code, { opening_wdv: v })}
                              />
                            )}
                          </td>
                          <td style={numTd}>{fmt(cfg.additions_total)}</td>
                          <td style={numTd}>{fmt(cfg.disposals_nbv_total)}</td>
                          <td style={numTd}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              {cfg.rate_source === 'LIFE' && (
                                <span style={fromLifeChipStyle}
                                  title={`Derived from useful life ${cfg.useful_life_years ?? '—'} yrs and residual ${residual}% (${cfg.method_effective ?? 'WDV'}). Type a rate to override.`}>
                                  from life
                                </span>
                              )}
                              {cfg.rate_source === 'IT' && (
                                <span style={fromLifeChipStyle}
                                  title="Book rates follow Income-tax rates (Settings) — this is the class's IT block rate. Type a rate to keep this class on its own rate.">
                                  IT rate
                                </span>
                              )}
                              {cfg.rate_source !== 'LIFE' && derivable && (
                                <RowBtn title={`Derive the ${cfg.method_effective ?? 'WDV'} rate from useful life ${cfg.useful_life_years} yrs and residual ${residual}%`}
                                  onClick={() => updateClass(code, { rate_from_life: true })}>
                                  <IconSystem.refresh size={12} />
                                </RowBtn>
                              )}
                              {draft.bookRatesFollowIt && isWdvLine(cfg) && cfg.rate_source !== 'IT'
                                && (cfg.rate_follows_it === false || cfg.rate_source === 'LIFE') && (
                                <RowBtn title="Follow the IT block rate again"
                                  onClick={() => updateClass(code, { rate_follows_it: true, rate_from_life: false })}>
                                  <span style={{ fontSize: '0.62rem', fontWeight: 700 }}>IT</span>
                                </RowBtn>
                              )}
                              <CellInput
                                value={cfg.book_rate ?? 0}
                                // Typing a rate is an override: stop deriving it and
                                // (when the calc follows IT rates) opt this line out.
                                onCommit={(v) => updateClass(code, {
                                  book_rate: v,
                                  rate_from_life: false,
                                  ...(draft.bookRatesFollowIt ? { rate_follows_it: false } : {}),
                                })}
                                width={64}
                                max={100}
                              />
                            </span>
                          </td>
                          <td style={numTd}>
                            {/* ADR-0024 N-2 — disclosed in Note 2's rate table; drives the rate only when "from life". */}
                            <CellInput
                              value={cfg.useful_life_years ?? 0}
                              onCommit={(v) => updateClass(code, { useful_life_years: v > 0 ? v : null })}
                              width={64}
                              max={200}
                            />
                          </td>
                          <td style={numTd}>
                            {cfg.dep_capped && (
                              <span style={{ ...hintChipStyle, marginLeft: 0, marginRight: 6 }}
                                title="Limited so the net book value does not fall below the residual value">
                                capped
                              </span>
                            )}
                            {fmt(cfg.dep_for_year_rounded)}
                          </td>
                          <td style={{ ...numTd, fontWeight: isSub ? 600 : 700 }}>{fmt(cfg.closing_wdv_rounded)}</td>
                        </tr>
                        {isSlm && (
                          <tr style={slmRowStyle}>
                            <td colSpan={9} style={{ ...tdStyle, paddingLeft: isSub ? 44 : 26, fontSize: '0.72rem' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                <span style={{ fontWeight: 700, color: 'var(--text-secondary)' }}>SLM basis</span>
                                <SlmField label="Opening cost ✎">
                                  <CellInput value={cfg.opening_gross_cost ?? 0} onCommit={(v) => updateClass(code, { opening_gross_cost: v })} />
                                </SlmField>
                                <SlmField label="Opening acc. dep. ✎">
                                  <CellInput value={cfg.opening_accum_dep ?? 0} onCommit={(v) => updateClass(code, { opening_accum_dep: v })} />
                                </SlmField>
                                <SlmField label="Fully depreciated cost ✎" title="Cost of assets in the opening cost that are already at residual value — excluded from the depreciable base">
                                  <CellInput value={cfg.fully_depreciated_cost ?? 0} onCommit={(v) => updateClass(code, { fully_depreciated_cost: v > 0 ? v : null })} />
                                </SlmField>
                                <SlmField label="Residual % ✎" title="Residual value as % of cost — depreciation stops when the net book value reaches it">
                                  <CellInput value={residual} onCommit={(v) => updateClass(code, { residual_pct: v })} width={56} max={100} />
                                </SlmField>
                                <span style={{ color: 'var(--text-secondary)' }}>→</span>
                                <SlmField label="Closing cost"><b>{fmt(cfg.closing_gross_cost)}</b></SlmField>
                                <SlmField label="Closing acc. dep."><b>{fmt(cfg.closing_accum_dep_rounded)}</b></SlmField>
                              </div>
                            </td>
                          </tr>
                        )}
                        {!isSub && subEditor?.parentCode === code && (
                          <tr style={subRowStyle}>
                            <td style={{ ...tdStyle, paddingLeft: 26 }} colSpan={5}>
                              <span style={{ color: 'var(--text-secondary)', marginRight: 6 }}>↳ New sub-class:</span>
                              <input
                                type="text"
                                className="form-input"
                                style={{ width: 220, padding: '2px 6px', fontSize: '0.78rem', display: 'inline-block' }}
                                placeholder="e.g. Factory Building"
                                value={subEditor.name}
                                autoFocus
                                onChange={(e) => setSubEditor(s => (s ? { ...s, name: e.target.value } : s))}
                                onKeyDown={(e) => { if (e.key === 'Enter') handleConfirmSubClass(); if (e.key === 'Escape') setSubEditor(null); }}
                              />
                            </td>
                            <td style={numTd}>
                              <input
                                type="number"
                                className="form-input"
                                style={{ width: 64, padding: '2px 6px', fontSize: '0.78rem', textAlign: 'right' }}
                                placeholder="Rate %"
                                value={subEditor.rate}
                                min={0}
                                max={100}
                                step="0.01"
                                onChange={(e) => setSubEditor(s => (s ? { ...s, rate: e.target.value } : s))}
                                onKeyDown={(e) => { if (e.key === 'Enter') handleConfirmSubClass(); if (e.key === 'Escape') setSubEditor(null); }}
                              />
                            </td>
                            <td style={{ ...tdStyle, whiteSpace: 'nowrap' }} colSpan={3}>
                              <button type="button" className="btn btn-primary btn-sm" onClick={handleConfirmSubClass}>Add</button>
                              <button type="button" className="btn btn-secondary btn-sm" style={{ marginLeft: 6 }} onClick={() => setSubEditor(null)}>Cancel</button>
                            </td>
                          </tr>
                        )}
                        {expanded && entries.map(e => e.kind === 'addition' ? (
                          <tr key={`a-${e.a.localId}`} style={subRowStyle} onClick={() => setAdditionModal({ open: true, entry: e.a })}>
                            <td style={{ ...tdStyle, paddingLeft: 28, fontSize: '0.72rem' }}>+ {e.a.description} · {fmtDate(e.a.additionDate)}</td>
                            <td style={tdStyle} />
                            <td style={tdStyle} />
                            <td style={{ ...numTd, fontSize: '0.72rem' }}>{fmt(e.a.amount)}</td>
                            <td style={tdStyle} />
                            <td style={tdStyle} />
                            <td style={tdStyle} />
                            <td style={{ ...numTd, fontSize: '0.72rem' }}>{fmt(e.a.bookDepRounded)}</td>
                            <td style={tdStyle} />
                          </tr>
                        ) : (
                          <tr key={`d-${e.d.localId}`} style={subRowStyle} onClick={() => setDisposalModal({ open: true, entry: e.d })}>
                            <td style={{ ...tdStyle, paddingLeft: 28, fontSize: '0.72rem' }}>− {e.d.description} · {fmtDate(e.d.disposalDate)}</td>
                            <td style={tdStyle} />
                            <td style={tdStyle} />
                            <td style={tdStyle} />
                            <td style={{ ...numTd, fontSize: '0.72rem' }}>{fmt(e.d.netBookValue)}</td>
                            <td style={tdStyle} />
                            <td style={tdStyle} />
                            <td style={{ ...numTd, fontSize: '0.72rem' }}>{e.d.depToDisposalRounded ? fmt(e.d.depToDisposalRounded) : ''}</td>
                            <td style={tdStyle} />
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                  <tr style={totalRowStyle}>
                    <td style={{ ...tdStyle, fontWeight: 700 }} colSpan={2}>Total</td>
                    <td style={{ ...numTd, fontWeight: 700 }}>{fmt(bookTotals.opening)}</td>
                    <td style={{ ...numTd, fontWeight: 700 }}>{fmt(bookTotals.additions)}</td>
                    <td style={{ ...numTd, fontWeight: 700 }}>{fmt(bookTotals.disposals)}</td>
                    <td style={tdStyle} />
                    <td style={tdStyle} />
                    <td style={{ ...numTd, fontWeight: 700 }}>{fmt(bookTotals.dep)}</td>
                    <td style={{ ...numTd, fontWeight: 700 }}>{fmt(bookTotals.closing)}</td>
                  </tr>
                </tbody>
              </table>
              <div style={footNoteStyle}>
                Opening WDV comes from the previous year's depreciation calc (roll-forward) or is
                entered manually — it is never derived from the mapped ledgers' TB balances.
                Method is chosen per class (a sub-class follows its class unless set). WDV depreciates
                the carrying amount; SLM depreciates cost, entered in the SLM basis row, and stops at
                the residual value. Rate is % per annum under the class's method — ↺ derives it from
                the useful life and residual value (Schedule II basis). Use the + on a class row to add a
                <b> sub-class</b> with its own rate (e.g. Factory Building under Buildings) —
                sub-classes exist only in this working; the note keeps one line per class.
              </div>
            </div>
          )}
        </Section>
        )}

        {/* 4 · IT SCHEDULE */}
        {activeSection === 'it' && (
        <Section
          title="Income Tax Depreciation (Block Basis)"
          subtitle="Authoritative s.32 computation — Opening WDV and Rate % editable here (✎); the ≥180/<180-day split derives from put-to-use dates."
          action={availableBlockOptions.length > 0 ? (
            <select
              className="form-select"
              style={{ fontSize: '0.78rem', padding: '4px 8px', maxWidth: 240 }}
              value=""
              onChange={(e) => { if (e.target.value) addItBlockRow(e.target.value); }}
            >
              <option value="">+ Add IT block…</option>
              {availableBlockOptions.map(o => (
                <option key={String(o.id)} value={String(o.id)}>{o.enumValue}</option>
              ))}
            </select>
          ) : undefined}
        >
          {draft.itBlocks.length === 0 ? (
            <EmptyNote text="No IT blocks yet. Blocks appear automatically when additions/disposals reference them; add a block manually to enter its opening WDV (year 1)." />
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <Th>IT Block</Th><Th right>Opening WDV ✎</Th><Th right>Add ≥180d</Th><Th right>Add &lt;180d</Th>
                    <Th right>Proceeds</Th><Th right>Rate % ✎</Th><Th>Exhausted ✎</Th><Th right>IT Dep.</Th>
                    <Th right>IT Profit/(Loss)</Th><Th right>Closing WDV</Th><Th> </Th>
                  </tr>
                </thead>
                <tbody>
                  {draft.itBlocks.map(b => {
                    const key = String(b.itBlockEnumId);
                    const blockAdditions = draft.additions.filter(a => String(a.itBlockEnumId) === key);
                    const blockDisposals = draft.disposals.filter(d => String(d.itBlockEnumId) === key);
                    const referenced = blockAdditions.length > 0 || blockDisposals.length > 0;
                    const ceased = b.isBlockNegative || b.isMarkedExhausted;
                    const blockExpanded = expandedBlocks.has(key);
                    return (
                      <React.Fragment key={key}>
                      <tr>
                        <td style={{ ...tdStyle, cursor: referenced ? 'pointer' : 'default' }}
                            onClick={() => referenced && toggleBlock(key)}
                            title={referenced ? 'Click to review the additions/disposals in this block' : undefined}>
                          {referenced && (
                            <span style={{ marginRight: 6, display: 'inline-block', transform: blockExpanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.12s' }}>▸</span>
                          )}
                          {b.label ?? b.enumKey ?? '—'}
                          {b.isBlockNegative && (
                            <span style={{ ...hintChipStyle, background: '#fee2e2', color: '#991b1b' }}
                              title="Proceeds exceed opening + additions — block exhausted automatically; excess shown as IT Profit, closing WDV 0">
                              exhausted · profit
                            </span>
                          )}
                          {!b.isBlockNegative && b.isMarkedExhausted && (
                            <span style={hintChipStyle}
                              title="Marked exhausted — remaining balance shown as IT Loss, closing WDV 0">
                              exhausted · loss
                            </span>
                          )}
                        </td>
                        <td style={numTd}>
                          <CellInput value={b.openingWdv} onCommit={(v) => updateItBlock(key, { openingWdv: v })} />
                        </td>
                        <td style={numTd}>{fmt(b.additionsGe180)}</td>
                        <td style={numTd}>{fmt(b.additionsLt180)}</td>
                        <td style={numTd}>{fmt(b.disposalProceeds)}</td>
                        <td style={numTd}>
                          <CellInput value={b.rate} onCommit={(v) => updateItBlock(key, { rate: v })} width={64} max={100} />
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={ceased}
                            disabled={b.isBlockNegative}
                            onChange={(e) => updateItBlock(key, { isMarkedExhausted: e.target.checked })}
                            style={{ cursor: b.isBlockNegative ? 'not-allowed' : 'pointer' }}
                            title={b.isBlockNegative
                              ? 'Auto-exhausted — proceeds exceed the block'
                              : 'Mark the block as ceased (all assets disposed); the remaining balance becomes an IT loss'}
                          />
                        </td>
                        <td style={numTd}>{fmt(b.itDepRounded)}</td>
                        <td style={{ ...numTd, color: (b.itProfitLoss ?? 0) < 0 ? '#dc2626' : (b.itProfitLoss ?? 0) > 0 ? '#16a34a' : undefined }}>
                          {b.itProfitLoss == null
                            ? '—'
                            : b.itProfitLoss < 0 ? `(${fmt(Math.abs(b.itProfitLoss))})` : fmt(b.itProfitLoss)}
                        </td>
                        <td style={{ ...numTd, fontWeight: 700 }}>{fmt(b.closingWdvRounded)}</td>
                        <td style={tdStyle}>
                          {!referenced && (
                            <RowBtn title="Remove block row" danger onClick={() => removeItBlockRow(key)}>
                              <IconSystem.x size={13} />
                            </RowBtn>
                          )}
                        </td>
                      </tr>
                      {/* Drill-down — the entries this block's figures derive from.
                          Amounts land under their computation column (≥180 / <180 /
                          Proceeds) so the block totals verify by eye. */}
                      {blockExpanded && blockAdditions.map(a => (
                        <tr key={`ba-${a.localId}`} style={subRowStyle} onClick={() => setAdditionModal({ open: true, entry: a })}>
                          <td style={{ ...tdStyle, paddingLeft: 28, fontSize: '0.72rem' }}>
                            + {a.description} · {clsName(entryConfigKey(a))} · put to use {fmtDate(a.putToUseDate)}
                            {a.isLt180Days && <span style={lt180ChipStyle}>&lt;180d</span>}
                          </td>
                          <td style={tdStyle} />
                          <td style={{ ...numTd, fontSize: '0.72rem' }}>{a.isLt180Days === false ? fmt(a.amount) : ''}</td>
                          <td style={{ ...numTd, fontSize: '0.72rem' }}>{a.isLt180Days === true ? fmt(a.amount) : ''}</td>
                          <td style={tdStyle} />
                          <td style={tdStyle} />
                          <td style={tdStyle} />
                          <td style={{ ...numTd, fontSize: '0.72rem', color: 'var(--text-secondary)' }}>{fmt(a.itDepIndicativeRounded)}</td>
                          <td style={tdStyle} />
                          <td style={tdStyle} />
                          <td style={tdStyle} />
                        </tr>
                      ))}
                      {blockExpanded && blockDisposals.map(d => (
                        <tr key={`bd-${d.localId}`} style={subRowStyle} onClick={() => setDisposalModal({ open: true, entry: d })}>
                          <td style={{ ...tdStyle, paddingLeft: 28, fontSize: '0.72rem' }}>
                            − {d.description} · {clsName(entryConfigKey(d))} · disposed {fmtDate(d.disposalDate)}
                          </td>
                          <td style={tdStyle} />
                          <td style={tdStyle} />
                          <td style={tdStyle} />
                          <td style={{ ...numTd, fontSize: '0.72rem' }}>{fmt(d.disposalProceeds)}</td>
                          <td style={tdStyle} />
                          <td style={tdStyle} />
                          <td style={tdStyle} />
                          <td style={tdStyle} />
                          <td style={tdStyle} />
                          <td style={tdStyle} />
                        </tr>
                      ))}
                      </React.Fragment>
                    );
                  })}
                  <tr style={totalRowStyle}>
                    <td style={{ ...tdStyle, fontWeight: 700 }}>Total</td>
                    <td style={{ ...numTd, fontWeight: 700 }}>{fmt(itTotals.opening)}</td>
                    <td style={{ ...numTd, fontWeight: 700 }}>{fmt(itTotals.ge)}</td>
                    <td style={{ ...numTd, fontWeight: 700 }}>{fmt(itTotals.lt)}</td>
                    <td style={{ ...numTd, fontWeight: 700 }}>{fmt(itTotals.proceeds)}</td>
                    <td style={tdStyle} />
                    <td style={tdStyle} />
                    <td style={{ ...numTd, fontWeight: 700 }}>{fmt(itTotals.dep)}</td>
                    <td style={{ ...numTd, fontWeight: 700 }}>
                      {itTotals.pl < 0 ? `(${fmt(Math.abs(itTotals.pl))})` : fmt(itTotals.pl)}
                    </td>
                    <td style={{ ...numTd, fontWeight: 700 }}>{fmt(itTotals.closing)}</td>
                    <td style={tdStyle} />
                  </tr>
                </tbody>
              </table>
              <div style={footNoteStyle}>
                ≥180 / &lt;180-day split derives from put-to-use dates; proceeds from the disposals register.
                Opening WDV: from the previous year's depreciation calc or manual entry.
                Block cessation: proceeds exceeding the block auto-exhaust it (excess = IT Profit); tick
                "Exhausted" when a block ceases with a balance remaining (remainder = IT Loss). Either way
                IT depreciation stops and the closing WDV becomes zero. IT Profit/(Loss) is a display
                figure for the tax computation — it is not pushed to any note. The Print and Excel exports
                include this schedule in the <b>Form 3CD clause 18</b> layout (block-wise, with each addition's
                date put to use and each sale's date).
              </div>
            </div>
          )}
        </Section>
        )}

        {/* 5 · DEFERRED TAX */}
        {activeSection === 'dt' && (
        <Section
          title="Deferred Tax (Depreciation Timing Difference)"
          subtitle="Positive = deferred tax asset; a liability shows in (brackets). Tax rate comes from Settings ⚙."
        >
          {!draft.dtEnabled ? (
            <EmptyNote text="Deferred tax is switched off for this engagement (Settings ⚙) — no position or charge is computed, and the Deferred Tax notes show no cross-check. Switch it on in Settings if the entity recognises deferred tax." />
          ) : (<>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '0.75rem', padding: '0.25rem 0.25rem 0.5rem' }}>
            <DtItem label="Closing book WDV" value={fmt(bookTotals.closing)} />
            <DtItem label="Closing IT WDV" value={fmt(itTotals.closing)} />
            <DtItem label="Timing difference (IT − Book)" value={fmtBracket(itTotals.closing - bookTotals.closing)} />
            <DtItem label="Tax rate" value={draft.taxRate != null ? `${draft.taxRate}%` : 'set in Settings ⚙'} />
            <div>
              <div style={dtLabelStyle} title="Positive = deferred tax asset; enter a liability as a negative value">
                Opening DTA/(DTL) ✎
              </div>
              <CellInput
                value={draft.dtOpeningPosition ?? 0}
                onCommit={(v) => applyDraft(prev => ({ ...prev, dtOpeningPosition: v }))}
                width={130}
                allowNegative
              />
            </div>
            <DtItem label="Closing DTA/(DTL)" value={fmtBracket(draft.dtClosingPositionRounded)} strong />
            <DtItem
              label="Charge/(credit) for year"
              value={fmtBracket(draft.dtChargeForYearRounded)}
              strong
            />
          </div>
          <div style={footNoteStyle}>
            Only the depreciation timing difference is computed here — other DT
            components stay manual in the note.
          </div>
          </>)}
        </Section>
        )}

        {/* 6 · BOOK VS TAX RECONCILIATION (rev 8) */}
        {activeSection === 'recon' && (
        <Section
          title="Book vs Tax Reconciliation"
          subtitle="For the income-tax computation — depreciation and disposal results per books against the Income-tax Act, by IT block."
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '0.75rem', padding: '0.25rem 0.25rem 0.75rem' }}>
            <DtItem label="Depreciation per books (add back)" value={fmt(recon.totals.bookDep)} />
            <DtItem label="Depreciation per IT Act (deduct)" value={fmt(recon.totals.itDep)} />
            <DtItem label="Book profit/(loss) on sale (reverse)" value={fmtBracket(recon.totals.bookProfitLoss)} />
            <DtItem label="Net adjustment to book profit" value={fmtBracket(recon.netAdjustmentToProfit)} strong />
            <DtItem label="STCG/(STCL) u/s 50 (capital gains)" value={fmtBracket(recon.totals.s50GainLoss)} strong />
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <Th>IT Block</Th><Th right>Dep. per books</Th><Th right>Dep. per IT</Th><Th right>Difference</Th>
                  <Th right>Book P/(L) on sale</Th><Th right>s.50 gain/(loss)</Th><Th right>Closing book WDV</Th><Th right>Closing tax WDV</Th>
                </tr>
              </thead>
              <tbody>
                {recon.rows.length === 0 && (
                  <tr><td colSpan={8} style={tdStyle}><EmptyNote text="Nothing to reconcile yet — enter the book schedule and IT blocks." /></td></tr>
                )}
                {recon.rows.map(r => (
                  <tr key={r.itBlockEnumId ?? 'none'}>
                    <td style={{ ...tdStyle, color: r.itBlockEnumId == null ? 'var(--text-secondary)' : undefined }}>{r.label}</td>
                    <td style={numTd}>{fmt(r.bookDep)}</td>
                    <td style={numTd}>{fmt(r.itDep)}</td>
                    <td style={numTd}>{fmtBracket(r.bookDep - r.itDep)}</td>
                    <td style={numTd}>{fmtBracket(r.bookProfitLoss)}</td>
                    <td style={numTd}>{fmtBracket(r.s50GainLoss)}</td>
                    <td style={numTd}>{fmt(r.bookClosingWdv)}</td>
                    <td style={numTd}>{fmt(r.itClosingWdv)}</td>
                  </tr>
                ))}
                <tr style={totalRowStyle}>
                  <td style={{ ...tdStyle, fontWeight: 700 }}>Total</td>
                  <td style={{ ...numTd, fontWeight: 700 }}>{fmt(recon.totals.bookDep)}</td>
                  <td style={{ ...numTd, fontWeight: 700 }}>{fmt(recon.totals.itDep)}</td>
                  <td style={{ ...numTd, fontWeight: 700 }}>{fmtBracket(recon.totals.bookDep - recon.totals.itDep)}</td>
                  <td style={{ ...numTd, fontWeight: 700 }}>{fmtBracket(recon.totals.bookProfitLoss)}</td>
                  <td style={{ ...numTd, fontWeight: 700 }}>{fmtBracket(recon.totals.s50GainLoss)}</td>
                  <td style={{ ...numTd, fontWeight: 700 }}>{fmt(recon.totals.bookClosingWdv)}</td>
                  <td style={{ ...numTd, fontWeight: 700 }}>{fmt(recon.totals.itClosingWdv)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div style={footNoteStyle}>
            Net adjustment = depreciation per books − depreciation per IT Act − book profit on sale
            (a book loss adds back) — assuming the book figures were charged to profit and loss.
            On a block basis a sale only reduces the block; a gain or loss arises for tax only when a
            block ceases (s.50 short-term capital gain/loss, from the IT Schedule). Book figures sit on a
            block through the class's default IT block (a sub-class uses its parent's); disposals through
            the block chosen on the disposal. Classes with no default block (e.g. land) show as "Not mapped".
          </div>
        </Section>
        )}
      </div>

      {/* modals + dialogs */}
      <FsaDepAdditionModal
        isOpen={additionModal.open}
        mode={additionModal.entry ? 'edit' : 'create'}
        entry={additionModal.entry}
        context={context}
        classConfig={draft.classConfig}
        roundingPrecision={p}
        bookProrataConvention={draft.bookProrataConvention}
        onSave={handleSaveAddition}
        onClose={() => setAdditionModal({ open: false, entry: null })}
      />
      <FsaDepDisposalModal
        isOpen={disposalModal.open}
        mode={disposalModal.entry ? 'edit' : 'create'}
        entry={disposalModal.entry}
        context={context}
        classConfig={draft.classConfig}
        roundingPrecision={p}
        bookProrataConvention={draft.bookProrataConvention}
        depToDisposalDate={draft.depToDisposalDate}
        onSave={handleSaveDisposal}
        onClose={() => setDisposalModal({ open: false, entry: null })}
      />
      <FsaDepSettingsModal
        isOpen={settingsOpen}
        settings={{
          roundingPrecision: draft.roundingPrecision,
          taxRate: draft.taxRate,
          bookProrataConvention: draft.bookProrataConvention,
          bookRatesFollowIt: draft.bookRatesFollowIt,
          dtEnabled: draft.dtEnabled,
          depToDisposalDate: draft.depToDisposalDate,
        }}
        isNceTemplate={context.isNceTemplate}
        onSave={handleApplySettings}
        onClose={() => setSettingsOpen(false)}
      />
      <ConfirmDialog
        open={deleteTarget != null}
        title={`Remove ${deleteTarget?.kind === 'disposal' ? 'Disposal' : 'Addition'}`}
        message={`Remove "${deleteTarget?.label ?? ''}" from the register? This is saved immediately.`}
        confirmLabel="Remove"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// small components
// ---------------------------------------------------------------------------

/** Full-height panel for the active sub-tab — header row + own scroll area. */
const Section: React.FC<{
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, subtitle, action, children }) => (
  <div style={sectionStyle}>
    <div style={sectionHeaderStyle}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: '0.875rem', letterSpacing: 0.2, color: 'var(--text-primary)' }}>{title}</div>
        {subtitle && (
          <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: 2 }}>{subtitle}</div>
        )}
      </div>
      <div style={{ flex: 1 }} />
      {action}
    </div>
    <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '0.75rem 1rem 1rem' }}>{children}</div>
  </div>
);

/** Segmented sub-tab pill with optional count badge / warning dot. */
const SubTab: React.FC<{
  label: string;
  active: boolean;
  onClick: () => void;
  badge?: string;
  warn?: boolean;
}> = ({ label, active, onClick, badge, warn }) => (
  <button
    type="button"
    onClick={onClick}
    style={active ? subTabActiveStyle : subTabStyle}
  >
    {label}
    {badge != null && (
      <span style={{
        marginLeft: 6, padding: '0 6px', borderRadius: 999, fontSize: '0.64rem', fontWeight: 700,
        background: active ? 'rgba(255,255,255,0.25)' : 'var(--bg-secondary)',
        color: active ? 'white' : 'var(--text-secondary)',
      }}>
        {badge}
      </span>
    )}
    {warn && (
      <span style={{ marginLeft: 6, color: active ? '#fde68a' : '#d97706' }} title="Has hints — informational only">⚠</span>
    )}
  </button>
);

/** Numeric cell input — commits on blur/Enter so recompute runs once per edit. */
const CellInput: React.FC<{
  value: number;
  onCommit: (v: number) => void;
  width?: number;
  max?: number;
  allowNegative?: boolean;
}> = ({ value, onCommit, width = 110, max, allowNegative }) => {
  const [text, setText] = useState<string | null>(null);
  const commit = () => {
    if (text == null) return;
    let v = parseFloat(text);
    if (Number.isNaN(v)) v = 0;
    if (!allowNegative && v < 0) v = 0;
    if (max != null && v > max) v = max;
    onCommit(v);
    setText(null);
  };
  return (
    <input
      type="number"
      className="form-input"
      style={{ width, padding: '2px 6px', fontSize: '0.78rem', textAlign: 'right' }}
      value={text ?? String(value)}
      step="0.01"
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
      onClick={(e) => e.stopPropagation()}
    />
  );
};

const RowBtn: React.FC<{ title: string; danger?: boolean; onClick: () => void; children: React.ReactNode }> = ({ title, danger, onClick, children }) => (
  <button
    type="button"
    title={title}
    onClick={(e) => { e.stopPropagation(); onClick(); }}
    style={{
      background: 'transparent', border: 'none', cursor: 'pointer', padding: '2px 4px',
      color: danger ? '#dc2626' : 'var(--text-secondary)',
    }}
  >
    {children}
  </button>
);

const Th: React.FC<{ right?: boolean; children: React.ReactNode }> = ({ right, children }) => (
  <th style={{ ...thStyle, textAlign: right ? 'right' : 'left' }}>{children}</th>
);

const DtItem: React.FC<{ label: string; value: string; strong?: boolean }> = ({ label, value, strong }) => (
  <div>
    <div style={dtLabelStyle}>{label}</div>
    <div style={{ fontSize: '0.9rem', fontWeight: strong ? 700 : 600, fontVariantNumeric: 'tabular-nums', color: 'var(--text-primary)' }}>{value}</div>
  </div>
);

/** Labelled inline field in an SLM basis row (rev 7). */
const SlmField: React.FC<{ label: string; title?: string; children: React.ReactNode }> = ({ label, title, children }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }} title={title}>
    <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
    {children}
  </span>
);

const EmptyNote: React.FC<{ text: string }> = ({ text }) => (
  <div style={{ padding: '0.75rem', fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{text}</div>
);

const CenterNote: React.FC<{ text: string; isError?: boolean; onRetry?: () => void }> = ({ text, isError, onRetry }) => (
  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, height: '100%', color: isError ? '#dc2626' : 'var(--text-secondary)', fontSize: '0.85rem' }}>
    {text}
    {onRetry && <button type="button" className="btn btn-secondary btn-sm" onClick={onRetry}>Retry</button>}
  </div>
);

// ---------------------------------------------------------------------------
// helpers + styles
// ---------------------------------------------------------------------------

const isWdvLine = (cfg: DepClassConfig): boolean => (cfg.method_effective ?? 'WDV') === 'WDV';

const blockLabel = (context: DepCalcContext, enumId: ID | null): string => {
  if (enumId == null) return '—';
  const opt = context.itBlockOptions.find(o => String(o.id) === String(enumId));
  return opt?.enumValue ?? '—';
};

const fmtDate = (iso: string): string => {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
};

const toolbarStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '0.5rem 0.75rem',
  borderBottom: '1px solid var(--border-color)',
  background: 'var(--bg-secondary)',
  flexWrap: 'wrap',
};
const sectionStyle: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  border: '1px solid var(--border-color)',
  borderRadius: 8,
  background: 'var(--bg-primary)',
  overflow: 'hidden',
};
const sectionHeaderStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 12,
  padding: '0.65rem 1rem',
  background: 'var(--bg-secondary)',
  borderBottom: '1px solid var(--border-color)',
  flexShrink: 0,
};
const subTabBarStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6,
  padding: '0.5rem 0.75rem 0',
  flexWrap: 'wrap',
  flexShrink: 0,
};
const subTabStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center',
  padding: '6px 14px',
  border: '1px solid var(--form-border-color)',
  borderRadius: 999,
  background: 'transparent',
  color: 'var(--text-primary)',
  cursor: 'pointer',
  fontSize: '0.8rem',
  fontWeight: 600,
  whiteSpace: 'nowrap',
};
const subTabActiveStyle: React.CSSProperties = {
  ...subTabStyle,
  background: 'var(--primary-color, #2563eb)',
  color: 'white',
  borderColor: 'var(--primary-color, #2563eb)',
};
const tableStyle: React.CSSProperties = {
  width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem',
};
const thStyle: React.CSSProperties = {
  padding: '8px 10px',
  borderBottom: '2px solid var(--border-color)',
  background: 'var(--bg-secondary)',
  fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.4,
  color: 'var(--text-secondary)', whiteSpace: 'nowrap',
  position: 'sticky', top: 0, zIndex: 1,
};
const tdStyle: React.CSSProperties = {
  padding: '7px 10px',
  borderBottom: '1px solid var(--border-color)',
  color: 'var(--text-primary)',
  verticalAlign: 'middle',
};
const numTd: React.CSSProperties = {
  ...tdStyle,
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
};
const groupRowStyle: React.CSSProperties = {
  background: 'var(--bg-secondary)',
};
const subRowStyle: React.CSSProperties = {
  background: 'var(--bg-secondary)',
  cursor: 'pointer',
};
const totalRowStyle: React.CSSProperties = {
  background: 'var(--bg-secondary)',
};
const hintChipStyle: React.CSSProperties = {
  display: 'inline-block', marginLeft: 8, padding: '1px 8px', borderRadius: 999,
  background: '#fef3c7', color: '#92400e', fontSize: '0.66rem', fontWeight: 600,
  whiteSpace: 'nowrap',
};
const dirtyChipStyle: React.CSSProperties = {
  display: 'inline-block', padding: '1px 8px', borderRadius: 999,
  background: '#dbeafe', color: '#1e40af', fontSize: '0.66rem', fontWeight: 600,
};
const savedChipStyle: React.CSSProperties = {
  display: 'inline-block', padding: '1px 8px', borderRadius: 999,
  background: '#dcfce7', color: '#166534', fontSize: '0.66rem', fontWeight: 600,
};
const lt180ChipStyle: React.CSSProperties = {
  display: 'inline-block', marginLeft: 4, padding: '0 4px', borderRadius: 4,
  background: '#fef3c7', color: '#92400e', fontSize: '0.6rem', fontWeight: 600,
};
const subClassChipStyle: React.CSSProperties = {
  display: 'inline-block', marginLeft: 6, padding: '0 6px', borderRadius: 4,
  background: 'var(--bg-secondary)', color: 'var(--text-secondary)',
  border: '1px solid var(--border-color)', fontSize: '0.62rem', fontWeight: 600,
};
const methodSelectStyle: React.CSSProperties = {
  fontSize: '0.75rem', padding: '2px 6px', width: 'auto', minWidth: 64,
};
const slmRowStyle: React.CSSProperties = {
  background: 'var(--bg-secondary)',
};
const fromLifeChipStyle: React.CSSProperties = {
  display: 'inline-block', padding: '0 5px', borderRadius: 4,
  background: 'var(--bg-secondary)', color: 'var(--text-secondary)',
  border: '1px solid var(--border-color)', fontSize: '0.6rem', fontWeight: 600, whiteSpace: 'nowrap',
};
const policyHintStyle: React.CSSProperties = {
  marginBottom: 8, padding: '6px 10px', borderRadius: 6,
  background: '#fef3c7', color: '#92400e', fontSize: '0.74rem', lineHeight: 1.4,
};
const footNoteStyle: React.CSSProperties = {
  marginTop: 6, fontSize: '0.68rem', color: 'var(--text-secondary)', lineHeight: 1.4,
};
const dtLabelStyle: React.CSSProperties = {
  fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.4,
  color: 'var(--text-secondary)', marginBottom: 2,
};

export default FsaDepreciationTab;
